import React, { useState }       from "react";
import axios                     from "axios";
import { Link, useLocation, useNavigate }     from "react-router-dom";
import { GoogleLogin }           from "@react-oauth/google";
import ReCAPTCHA                 from "react-google-recaptcha";

import Button                    from "../../components/Button/Button3";
import LoadingOverlay            from "../../components/LoadingOverlay/LoadingOverlay";
import Modal                     from "../../components/Modal/Modal";
import Seo                       from "../../components/Seo/Seo";
import PrivacyPolicy             from "../../components/PrivacyPolicy/PrivacyPolicy";
import Terms                     from "../../components/PrivacyPolicy/Terms";

import homeBg                    from "../../assets/images/home_background.avif";
import "./Register.css";
import { ROUTES } from "../../utils/appRoutes";
import {
  buildAuthPathWithNext,
  getPostAuthFallbackPath,
  getSafeNextFromLocation,
} from "../../utils/authRedirect";
import { getEmbeddedBrowserName } from "../../utils/embeddedBrowser";
import { isValidEmail } from "../../utils/isValidEmail";


function Register({ onLogin }) {
  /* ───── form state ───── */
  const [step,setStep]                   = useState(1);
  const [firstName,setFirstName]         = useState("");
  const [lastName,setLastName]           = useState("");
  const [email,setEmail]                 = useState("");
  const [pwd,setPwd]                     = useState("");
  const [pwd2,setPwd2]                   = useState("");
  const [showPasswords,setShowPasswords] = useState(false);
  const [privacy,setPrivacy]             = useState(false);
  const [captcha,setCaptcha]             = useState(null);

  /* ───── UX state ───── */
  const [busy,setBusy]       = useState(false);   // e-mail register
  const [gBusy,setGBusy]     = useState(false);   // Google register
  const [msg,setMsg]         = useState("");
  const [modal,setModal]     = useState(null);    // null | component fn

  const nav = useNavigate();
  const location = useLocation();
  const nextPath = getSafeNextFromLocation(location, getPostAuthFallbackPath(location));
  const loginPath = buildAuthPathWithNext(ROUTES.LOGIN, nextPath);
  const confirmPath = buildAuthPathWithNext(ROUTES.CONFIRM, nextPath);
  const embeddedBrowserName = getEmbeddedBrowserName();
  const showEmbeddedBrowserNotice = Boolean(embeddedBrowserName);

  /* ── helpers ── */
  const saveTokens = ({ idToken, accessToken, refreshToken })=>{
    localStorage.setItem("idToken",idToken);
    localStorage.setItem("accessToken",accessToken);
    localStorage.setItem("refreshToken",refreshToken);
    onLogin && onLogin(idToken, accessToken, refreshToken);
  };

  /* ── Google fast path ── */
  const handleGoogle = async ({ credential })=>{
    setGBusy(true);
    try{
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/google`,
        { credential }
      );
      saveTokens({
        idToken     : data.cognito.idToken,
        accessToken : data.cognito.accessToken,
        refreshToken: data.cognito.refreshToken,
      });
      localStorage.setItem("userId",   data.user._id);
      localStorage.setItem("firstName",data.user.firstName || "");
      nav(nextPath, { replace: true });
    }catch(e){
      setMsg(e.response?.data?.error || "Google sign-in failed.");
    }finally{
      setGBusy(false);
    }
  };

  /* ── e-mail register ── */
  const handleRegister = async ()=>{
    if(!privacy)           return setMsg("Accept the Privacy Policy first.");
    if(!pwd || !pwd2)      return setMsg("Fill in both password fields.");
    if(pwd.length<8)       return setMsg("Password must be ≥ 8 chars.");
    if(pwd!==pwd2)         return setMsg("Passwords do not match.");
    if(!captcha)           return setMsg("Please solve the captcha.");

    setBusy(true);
    try{
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`,{
        first_name:firstName, last_name:lastName, email,
        password:pwd, captcha
      });
      nav(confirmPath,{ state:{ email,password:pwd, next: nextPath }});
    }catch(e){
      setMsg(e.response?.data?.error || "Registration failed.");
    }finally{
      setBusy(false);
    }
  };

  /* ── basic e-mail validators ── */
  const validMail = (value) => isValidEmail(value.trim());

  const nextStep = ()=>{
    if(!firstName||!lastName||!email) return setMsg("Fill all fields.");
    if(!validMail(email))   return setMsg("Enter a valid e-mail.");
    setStep(2); setMsg("");
  };

  /* ── JSX ── */
  return (
    <>
      <Seo
        title="Register | ZealFC"
        description="Create your ZealFC account to join football games, manage attendance, and book your next match."
        robots="noindex, nofollow"
        canonicalPath={ROUTES.REGISTER}
      />
      <div className="register-layout">
        <div className="register-image-container">
          <img src={homeBg} alt="" className="register-image"/>
          <div className="noise-effect"/>
        </div>

        {(busy || gBusy) && (
          <LoadingOverlay
            label={gBusy ? "Signing in with Google" : "Creating your account"}
          />
        )}

        <div className="register-form-container">
          <p className="register-title">{step===1?"TELL US ABOUT YOURSELF!":"Set Your Password"}</p>

          <form className="register-form" onSubmit={e=>e.preventDefault()}>
            {step===1 && (
              <>
                <div className="register-field">
                  <span>First Name:</span>
                  <input value={firstName} onChange={e=>setFirstName(e.target.value)}
                         placeholder="Enter your first name" className="register-input-line"/>
                </div>
                <div className="register-field">
                  <span>Last Name:</span>
                  <input value={lastName} onChange={e=>setLastName(e.target.value)}
                         placeholder="Enter your last name" className="register-input-line"/>
                </div>
                <div className="register-field">
                  <span>Email:</span>
                  <input value={email} onChange={e=>setEmail(e.target.value)}
                         placeholder="Enter your e-mail" type="email"
                         className="register-input-line"/>
                </div>

                <Button variant="small" primaryText="NEXT" onClick={nextStep}/>

                {showEmbeddedBrowserNotice ? (
                  <div className="auth-browser-notice" role="status">
                    <span className="auth-browser-notice-label">In-App Browser</span>
                    <p className="auth-browser-notice-copy">
                      Google sign-in is not supported inside {embeddedBrowserName}. Open this page
                      in Chrome or Safari to continue with Google, or finish registering with
                      your email here.
                    </p>
                  </div>
                ) : (
                  <div className="google-wrapper">
                    <GoogleLogin
                      theme="filled_black"
                      width="300"
                      text="signup_with"
                      onSuccess={handleGoogle}
                      onError={()=>setMsg("Google sign-in cancelled.")}
                    />
                  </div>
                )}
              </>
            )}

            {step===2 && (
              <>
                <div className="register-field">
                  <span>Password:</span>
                  <div className="password-container">
                    <input
                      type={showPasswords ? "text" : "password"}
                      value={pwd}
                      onChange={e=>setPwd(e.target.value)}
                      placeholder="Enter password"
                      className="register-input-line"
                    />
                    <span
                      className="toggle-password"
                      onClick={() => setShowPasswords(!showPasswords)}
                    >
                      {showPasswords ? "Hide" : "SHOW"}
                    </span>
                  </div>
                </div>
                <div className="register-field">
                  <span>Confirm Password:</span>
                  <div className="password-container">
                    <input
                      type={showPasswords ? "text" : "password"}
                      value={pwd2}
                      onChange={e=>setPwd2(e.target.value)}
                      placeholder="Confirm password"
                      className="register-input-line"
                    />
                    <span
                      className="toggle-password"
                      onClick={() => setShowPasswords(!showPasswords)}
                    >
                      {showPasswords ? "Hide" : "SHOW"}
                    </span>
                  </div>
                </div>

                <div className="register-field register-captcha-field">
                  <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                             onChange={setCaptcha}/>
                </div>

                <div className="register-field privacy-policy-checkbox">
                  <input id="pp" type="checkbox" checked={privacy}
                         onChange={e=>setPrivacy(e.target.checked)}/>
                  <label htmlFor="pp">
                    I agree to the&nbsp;
                    <span className="privacy-link" onClick={()=>{setModal(PrivacyPolicy);}}>
                      Privacy Policy
                    </span>&nbsp;and&nbsp;
                    <span className="privacy-link" onClick={()=>{setModal(Terms);}}>
                      Terms &amp; Conditions
                    </span>
                  </label>
                </div>

                <div className="button-row">
                  <Button variant="small" primaryText="Go Back" onClick={()=>setStep(1)}/>
                  <Button variant="small" primaryText={busy?"Registering…":"Register"}
                          onClick={handleRegister}/>
                </div>
              </>
            )}

            {msg && <p className="register-message">{msg}</p>}
            <div className="register-link">
              <span>Already have an account?</span>
              <Link to={loginPath} className="register-link-text"> Login</Link>
            </div>
          </form>
        </div>

        {modal && <Modal onClose={()=>setModal(null)} ContentComponent={modal}/>}
      </div>
    </>
  );
}

export default Register;
